/**
 * Pet Protection content — Sister Signal v1.1.
 *
 * Companion animals are routinely used as leverage in abusive relationships.
 * This content gives survivors language, a safety plan template, and the
 * resources to keep their animals safe.
 *
 * Spanish drafts marked // review pending.
 */

const PET_LESSONS = [
  {
    slug: "pets-as-leverage",
    title_en: "When Pets Become Leverage",
    title_es: "Cuando las Mascotas se Vuelven una Herramienta",
    sections_en: [
      {
        body: "Up to 71% of women in domestic-violence shelters report that an abuser threatened, hurt, or killed a pet to control them. If your partner uses your animal — even subtly — as a way to keep you compliant, you are not paranoid. This is a recognized pattern.",
      },
      {
        heading: "How it shows up",
        list: [
          "Threats: ‘If you leave, the dog goes.’",
          "Withholding food, vet care, or medication for the pet.",
          "Kicking, throwing, or ‘accidentally’ injuring the animal during fights.",
          "Making the pet a condition of staying: ‘he’s mine legally’ even when you adopted them together.",
          "Punishing the pet to punish you.",
        ],
      },
      {
        body: "Animals can’t tell anyone what they saw. Your love for them is real. Their safety is part of your safety.",
      },
    ],
    sections_es: [
      {
        body: "Hasta el 71% de las mujeres en refugios de violencia doméstica reportan que su pareja amenazó, lastimó o mató a una mascota para controlarlas. Si tu pareja usa a tu animal — incluso de forma sutil — para mantenerte en cumplimiento, no estás siendo paranoica. Es un patrón reconocido.",
      },
      {
        heading: "Cómo aparece",
        list: [
          "Amenazas: ‘Si te vas, el perro se va.’",
          "Negarle al animal comida, atención veterinaria o medicinas.",
          "Patadas, lanzamientos o ‘accidentes’ durante una pelea.",
          "Hacer del animal una condición para quedarte: ‘legalmente es mío’ aunque lo adoptaron juntos.",
          "Castigar a la mascota para castigarte a ti.",
        ],
      },
      {
        body: "Los animales no pueden contar lo que vieron. Tu amor por ellos es real. Su seguridad es parte de la tuya.",
      },
    ],
  },
  {
    slug: "pet-safety-plan",
    title_en: "Creating a Pet Safety Plan",
    title_es: "Crear un Plan de Seguridad para Mascotas",
    sections_en: [
      {
        heading: "The five-piece plan",
        list: [
          "ID your pet — chip, tag, recent photo on your phone showing both of you.",
          "Records — vaccine card, vet contact, microchip number, meds schedule. Save copies somewhere off-site.",
          "Ownership — adoption papers, vet bills, registration in your name where possible.",
          "Foster or safe haven — a trusted friend, family member, or DV-friendly program who could take them on short notice.",
          "Grab bag — leash, carrier, two days of food, meds, water bowl, comfort item. Hidden but reachable.",
        ],
      },
      {
        body: "If you need to leave in a hurry and can’t bring them, do not assume you’ll never see them again. Many shelters now coordinate emergency fostering specifically for survivors’ pets.",
      },
    ],
    sections_es: [
      {
        heading: "Plan de cinco partes",
        list: [
          "Identificación — chip, placa, foto reciente en tu celular donde aparezcan los dos.",
          "Registros — cartilla de vacunas, contacto del veterinario, número de microchip, horario de medicinas. Guarda copias fuera de casa.",
          "Propiedad — papeles de adopción, recibos del veterinario, registro a tu nombre cuando sea posible.",
          "Cuidador o refugio seguro — una amiga, familiar o programa amigable con sobrevivientes que pueda quedarse con tu mascota en corto plazo.",
          "Mochila de salida — correa, transportadora, dos días de comida, medicinas, plato de agua, un objeto que le dé consuelo. Escondida pero accesible.",
        ],
      },
      {
        body: "Si necesitas irte rápido y no puedes llevarlos, no asumas que no los volverás a ver. Muchos refugios coordinan hoy en día acogidas de emergencia específicamente para mascotas de sobrevivientes.",
      },
    ],
  },
  {
    slug: "vet-records-checklist",
    title_en: "Veterinary Records Checklist",
    title_es: "Lista de Registros Veterinarios",
    sections_en: [
      {
        heading: "Photograph or scan",
        list: [
          "Vaccine records (rabies especially — required by most shelters).",
          "Spay / neuter certificate.",
          "Microchip registration paper and chip number.",
          "Current prescriptions and dose schedule.",
          "Vet contact info and your pet’s patient ID.",
          "Adoption papers / proof of ownership.",
          "A recent photo with you and your pet together (proof of relationship).",
        ],
      },
      {
        body: "Save these to a cloud account he doesn’t access (a new email + cloud combo works well). Print one paper copy to a friend’s home or safe deposit box.",
      },
    ],
    sections_es: [
      {
        heading: "Fotografía o escanea",
        list: [
          "Cartilla de vacunas (en especial rabia — requerida por la mayoría de los refugios).",
          "Certificado de esterilización.",
          "Papel de registro del microchip y número de chip.",
          "Recetas actuales y horario de dosis.",
          "Contacto del veterinario y número de paciente.",
          "Papeles de adopción / prueba de propiedad.",
          "Una foto reciente donde aparezcan tú y tu mascota juntos (prueba de la relación).",
        ],
      },
      {
        body: "Guarda esto en una nube a la que él no tenga acceso (un correo nuevo + nube funciona bien). Imprime una copia en papel para casa de una amiga o una caja de seguridad.",
      },
    ],
  },
  {
    slug: "emergency-foster",
    title_en: "Emergency Foster Programs",
    title_es: "Programas de Acogida de Emergencia",
    sections_en: [
      {
        heading: "Where to look",
        list: [
          "RedRover Safe Escape grants — financial help for boarding when you leave.",
          "Safe Place for Pets (Sheltering Animals & Families Together) — a directory of DV programs that take pets too.",
          "Your local humane society — many run quiet ‘safe haven’ programs for survivors.",
          "Your vet — many will board for free or low-cost in confirmed DV cases.",
        ],
      },
      {
        body: "Call from a safe phone. Ask for the survivor or DV liaison. You can use first names only. They will not pressure you to leave today — they will just hold the option open.",
      },
    ],
    sections_es: [
      {
        heading: "Dónde buscar",
        list: [
          "RedRover Safe Escape — ayuda económica para hospedaje al salir.",
          "Safe Place for Pets (Sheltering Animals & Families Together) — directorio de programas de violencia doméstica que también reciben mascotas.",
          "La sociedad humanitaria local — muchas tienen programas discretos de ‘refugio seguro’ para sobrevivientes.",
          "Tu veterinario — muchos hospedan gratis o a bajo costo en casos confirmados de violencia.",
        ],
      },
      {
        body: "Llama desde un teléfono seguro. Pide hablar con la enlace de sobrevivientes o de violencia doméstica. Puedes usar solo tu primer nombre. No te van a presionar a salir hoy — solo van a dejar la opción abierta.",
      },
    ],
  },
  {
    slug: "pet-friendly-shelters",
    title_en: "Pet-Friendly Shelter Resources",
    title_es: "Refugios que Aceptan Mascotas",
    sections_en: [
      {
        heading: "National (US)",
        list: [
          "Safe Havens for Pets directory — saferplaceforpets.org",
          "RedRover.org — financial grants for pet boarding during a leave",
          "Domestic Shelters.org — search ‘allows pets’ filter by ZIP",
          "National Domestic Violence Hotline: 1-800-799-7233 — they can route you to a pet-friendly local resource",
        ],
      },
      {
        body: "If no local shelter takes pets, ask about partnerships. Many DV shelters now contract with local boarding facilities for short-term pet care while the survivor is in transition.",
      },
    ],
    sections_es: [
      {
        heading: "Nacional (EE. UU.)",
        list: [
          "Directorio Safe Havens for Pets — saferplaceforpets.org",
          "RedRover.org — apoyo económico para hospedar mascotas al salir",
          "Domestic Shelters.org — busca con el filtro ‘acepta mascotas’ por código postal",
          "Línea Nacional contra la Violencia Doméstica: 1-800-799-7233 — te canalizan a un recurso local pet-friendly",
        ],
      },
      {
        body: "Si ningún refugio local acepta mascotas, pregunta por convenios. Muchos refugios DV tienen acuerdos con hospedajes locales para cuidar a la mascota mientras la sobreviviente transita.",
      },
    ],
  },
];

export function listPetLessons() { return PET_LESSONS; }
export function getPetLesson(slug) { return PET_LESSONS.find((p) => p.slug === slug) || null; }
